import { useEffect, useState } from "react";
import { getListProduct } from "../../../services";
import { WEB_IT_EXPORT_LABEL, WEB_IT_MAX_DETAIL_ROWS } from "./webItExport";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";

// Only the list's total is read, so one row per request is enough.
const FIRST_PAGE = 1;
const COUNT_ONLY_LIMIT = 1;

const WITHIN_CEILING_MESSAGE = `ไฟล์ WEB_IT หนึ่งไฟล์รับสินค้าได้ไม่เกิน ${WEB_IT_MAX_DETAIL_ROWS} รายการ สินค้าที่ปิดไว้จะยังออกในไฟล์ของวันที่มีรายการกระทบยอด`;
const OVER_CEILING_MESSAGE = `เกินจำนวนที่ไฟล์ WEB_IT รับได้ (${WEB_IT_MAX_DETAIL_ROWS} รายการ) ระบบจะออกไฟล์ WEB_IT ไม่ได้ กรุณาปิด "${WEB_IT_EXPORT_LABEL}" ของสินค้าที่ไม่ได้ใช้แล้ว`;

interface WebItExportSummaryProps {
  /** Changes whenever a product may have been switched in or out of the export. */
  refreshKey: number;
}

/**
 * How many products are switched into the WEB_IT export against the most rows one file can
 * carry. A product switched off still ships on a day it has reconciliation rows, so a day's
 * file can hold more rows than this count, never fewer.
 */
export default function WebItExportSummary({
  refreshKey,
}: WebItExportSummaryProps) {
  const [exportedCount, setExportedCount] = useState<number | null>(null);

  useEffect(() => {
    // A slower earlier response must not overwrite the count of a later request.
    let ignored = false;

    const loadExportedCount = async () => {
      try {
        const {
          data: { data },
        } = await getListProduct(FIRST_PAGE, COUNT_ONLY_LIMIT, {
          allow_export: true,
        });
        if (!ignored) setExportedCount(data.count);
      } catch {
        // The axios interceptor reports the failure; a stale count must not stay on screen.
        if (!ignored) setExportedCount(null);
      }
    };

    loadExportedCount();

    return () => {
      ignored = true;
    };
  }, [refreshKey]);

  if (exportedCount === null) return null;

  const isOverCeiling = exportedCount > WEB_IT_MAX_DETAIL_ROWS;
  const severity = isOverCeiling ? "warning" : "info";
  const title = `สินค้าที่${WEB_IT_EXPORT_LABEL} ${exportedCount} / ${WEB_IT_MAX_DETAIL_ROWS} รายการ`;
  const message = isOverCeiling ? OVER_CEILING_MESSAGE : WITHIN_CEILING_MESSAGE;

  return (
    <Alert severity={severity} sx={{ mb: 2 }}>
      <AlertTitle>{title}</AlertTitle>
      {message}
    </Alert>
  );
}
